<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod'
import { Loader2, TriangleAlert } from 'lucide-vue-next'
import { configure, type FormContext, useForm } from 'vee-validate'
import { defineEmits, reactive, watch } from 'vue'
import * as z from 'zod'
import { useCreateCompany } from '@/composables/src/useCreateCompany'
import { useFormValidationSchemas } from '@/composables/src/useFormValidationSchemas'
import { Alert, AlertDescription } from '@/foundation/alert'
import { AutoForm } from '@/foundation/auto-form'
import PhoneInput from '@/foundation/auto-form/AutoFormPhoneNumber.vue'
import { Button } from '@/foundation/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/foundation/dialog'
import DialogCheck from '../components/DialogCheck.vue'
import DomainTags from './DomainTags.vue'

configure({
  bails: false,
  validateOnBlur: true,
  validateOnChange: true,
  validateOnInput: false,
  validateOnModelUpdate: true,
})

const { duplicateDomains } = useCreateCompany()
const { prefixSchema, addressSchema, phoneSchema, domainsSchema } = useFormValidationSchemas()

// Define Form Schema using zod
const schema = z.object({
  company: z
    .string({
      required_error: 'Company name is required.',
    })
    .min(1, { message: 'Company name is required.' }),
  prefix: prefixSchema,
  address: addressSchema,
  phone: phoneSchema,
  domains: domainsSchema,
  logo_light: z.any().optional(),
  logo_dark: z.any().optional(),
})

const fieldConfig = reactive({
  company: {
    label: 'Company Name',
    inputProps: {
      class: 'w-full lg:w-2/5',
      placeholder: 'Company Name',
    },
  },
  prefix: {
    label: 'Prefix',
    inputProps: {
      class: 'w-full lg:w-2/5',
      placeholder: 'Prefix',
    },
  },
  address: {
    label: 'Address',
    inputProps: {
      class: 'w-full lg:w-2/5',
      placeholder: 'Address',
    },
  },
  phone: {
    label: 'Phone',
    component: PhoneInput,
    inputProps: {
      class: 'w-full lg:w-2/5',
      autoDefaultCountry: false,
      defaultCountry: 'US',
    },
  },
  domains: {
    label: 'Domains',
    component: DomainTags,
    multiple: true,
    inputProps: {
      class: 'w-full lg:w-2/5',
    },
    duplicateDomains,
  },
  // logo_light: {
  //   label: 'Company Logo (Light Theme)',
  //   component: 'file',
  //   inputProps: {
  //     class: 'w-full lg:w-2/5',
  //   },
  // },
  // logo_dark: {
  //   label: 'Company Logo (Dark Theme)',
  //   component: 'file',
  //   inputProps: {
  //     class: 'w-full lg:w-2/5',
  //   },
  // },
})

const form: FormContext = useForm({
  validationSchema: toTypedSchema(schema),
  initialValues: {
    domains: [],
  },
})

const {
  isFormDisabled,
  isLoading,
  isSubmitting,
  handleConfirmDuplicate,
  handleCancelDuplicate,
  errorMessage,
  backendError,
  onSubmit,
  showDuplicateDialog,
} = useCreateCompany(form)

// --- Field-level Validation ---
const { values, setFieldValue } = form
const emit = defineEmits(['closeDialog'])

watch(
  () => values.prefix,
  (currentValue) => {
    if (currentValue && typeof currentValue === 'string') {
      const uppercaseValue = currentValue.toUpperCase()

      if (currentValue !== uppercaseValue) {
        setFieldValue('prefix', uppercaseValue)
      }
    }
  },
)
</script>

<template>
  <div class="flex flex-col w-full" :style="{ height: 'calc(100% - 70px)' }">
    <Alert v-if="backendError" variant="destructive" class="!bg-destructive mb-5 !flex items-center text-white">
      <TriangleAlert color="white" class="w-4 h-4" />
      <AlertDescription class="font-semibold whitespace-pre-line">
        {{ backendError }}
      </AlertDescription>
    </Alert>
    <Alert
      v-if="errorMessage && !backendError"
      variant="destructive"
      class="!bg-destructive mb-5 !flex items-center text-white"
    >
      <TriangleAlert color="white" class="w-4 h-4" />
      <AlertDescription class="font-semibold">
        {{ errorMessage }}
      </AlertDescription>
    </Alert>

    <div class="flex w-full">
      <AutoForm class="w-full space-y-6" :schema="schema" :field-config="fieldConfig" :form="form" @submit="onSubmit">
        <div>
          <div
            class="flex justify-end w-full gap-2 absolute bottom-0 p-4 rounded-b-sm right-0 border-t-2 border-border z-[100] bg-create-form-footer"
          >
            <DialogCheck :show-icon="true" @dialog-should-close="$emit('closeDialog')" />
            <slot v-bind="{ onSubmit }">
              <Button type="submit" :disabled="isFormDisabled" @click="isSubmitting = true">
                <Loader2 v-if="isLoading" class="w-4 h-4 mr-2 animate-spin" /> Create
              </Button>
            </slot>
          </div>
        </div>
      </AutoForm>
    </div>

    <!-- Duplicate confirmation dialog -->
    <Dialog v-model:open="showDuplicateDialog">
      <DialogTrigger class="hidden" />
      <DialogContent
        class="!w-[90vw] !max-w-[500px] !md:max-w-[500px] !lg:max-w-[500px] max-h-[85vh] p-[25px] pb-4 rounded-[6px]"
      >
        <DialogHeader>
          <DialogTitle>Duplicate Company Name</DialogTitle>
          <DialogDescription class="my-5">
            The system already contains a company with this name.
            <span class="font-semibold"> Would you like to continue and create a new one? </span>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" @click="handleCancelDuplicate">Cancel</Button>
          <Button @click="handleConfirmDuplicate">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>
